// [목적] /chat 좌측 "여행 설계 가이드" — 질문 하나씩 던지고 답하면 다음 질문이 이어 붙는 대화형 플로우.
// [이유] 2026-09-21 trip-guide: 첫 진입 화면에 방향성이 없어 무엇을 입력해야 할지 알 수 없다는 피드백.
//        답을 고르면 그 카드는 요약칩으로 접히고, 타이핑 인디케이터 뒤에 다음 카드가 슬라이드-인 한다.
//        마지막에 고른 값들을 한 문장으로 조립해 기존 입력창에 넣어준다(전송은 사용자가 누름).
// [사이드 이팩트] 답변 상태는 부모(ChatPage)가 소유 — 우측 GuidePreview 와 공유하기 위함.
//                onFill(sentence) 은 입력창 값만 채운다(자동 전송 금지 — 사용자 확인 후 전송).
// [수정 시 고려사항] 모든 단계에 '보기에 없으면 채팅창에' 안내(note)를 노출한다 — 빼지 말 것.
//                   단계 정의/문구/조립 규칙은 guideSteps.ts 가 단일 소스. 여기서 문구를 새로 만들지 않는다.
//                   44px 최소 터치 타깃 규칙 유지(2026-09-20 모바일 QA).

import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Box, ButtonBase, Stack, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { tokens } from '@gangwoncanvas/ui';

import { GuideCalendar } from './GuideCalendar.js';
import {
  BUDGET_DEFAULT,
  BUDGET_INPUT_MAX,
  BUDGET_MAX,
  BUDGET_MIN,
  BUDGET_PRESETS,
  BUDGET_STEP,
  COUNT_MAX,
  COUNT_MIN,
  GUIDE_STEPS,
  DAYS_OPTIONS,
  DETAIL_EXAMPLES,
  MOOD_MAX,
  TRANSPORT_OPTIONS,
  answerLabel,
  formatGuideDate,
  buildGuideSentence,
  budgetTier,
  RANGE_INPUT_MAX,
  RANGE_SLIDER_MAX,
  RANGE_SLIDER_MIN,
  RANGE_SLIDER_STEP,
  RANGE_UNLIMITED,
  rangePresets,
  resolveOptions,
  type GuideAnswers,
  type GuideOption,
  type GuideStep,
} from './guideSteps.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const SOFT_LINE = c.line.soft[L];
const PAPER0 = c.paper[0][L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
const BRAND_TEXT = c.brand[600][L];
const ACCENT = c.accent[500][L];
const ACCENT_TEXT = c.accent[600][L];
const WARM_TEXT = c.warm[600][L];
const BRONZE = c.badge.bronze.fg[L];
const MONO = 'JetBrains Mono, ui-monospace, monospace';
/** 다음 질문이 나오기 전 타이핑 인디케이터 노출 시간 */
const TYPING_MS = 600;

const TIER_COLOR = {
  light: ACCENT_TEXT,
  normal: BRAND_TEXT,
  rich: WARM_TEXT,
  luxury: BRONZE,
} as const;

export interface TripGuideProps {
  answers: GuideAnswers;
  onAnswersChange: (next: GuideAnswers) => void;
  /**
   * 완성 문장 제출 — 호출부(ChatPage)가 로그인 게이트를 거쳐 그대로 전송한다.
   * [2026-09-21 QA] 이전에는 입력창에 채우기만 했는데, 가이드 화면에서 채팅 입력창 자체를 빼기로 해
   * (사용자 요청) 버튼이 곧 전송이 됐다.
   */
  onSubmit: (sentence: string) => void;
  /** 모든 단계를 끝냈는지 부모에 알림(우측 완료 카드 노출용) */
  onCompletedChange?: (completed: boolean) => void;
}

/**
 * [목적] 가이드 본체 — 진행 게이지 + 단계 카드 스택 + 완성 카드
 * [이유] 대화 UI 관례대로 위에서 아래로 쌓이며, 답한 단계는 접혀서 맥락만 남는다.
 * [사이드 이팩트] setTimeout 으로 타이핑 연출 — 언마운트/되감기 시 정리한다.
 */
export function TripGuide({
  answers,
  onAnswersChange,
  onSubmit,
  onCompletedChange,
}: TripGuideProps): JSX.Element {
  const [stepIndex, setStepIndex] = useState(0);
  const [typing, setTyping] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const completed = stepIndex >= GUIDE_STEPS.length && !typing;

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );
  useEffect(() => {
    onCompletedChange?.(completed);
  }, [completed, onCompletedChange]);
  useEffect(() => {
    // jsdom 에는 scrollIntoView 가 없다(ChatPage.test.tsx) — 옵셔널 호출로 테스트 환경을 깨뜨리지 않는다
    bottomRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [stepIndex, typing]);

  const advance = useCallback(() => {
    setTyping(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setTyping(false);
      setStepIndex((i) => i + 1);
    }, TYPING_MS);
  }, []);

  const commit = useCallback(
    (next: Partial<GuideAnswers>) => {
      onAnswersChange({ ...answers, ...next });
      advance();
    },
    [advance, answers, onAnswersChange],
  );

  // [2026-09-21] 진행 없이 값만 갱신 — 'move' 단계가 이동수단/반경 두 값을 한 카드에서 받기 때문에
  //              첫 번째 선택에서 다음 단계로 넘어가면 안 된다.
  const patch = useCallback(
    (next: Partial<GuideAnswers>) => {
      onAnswersChange({ ...answers, ...next });
    },
    [answers, onAnswersChange],
  );

  /** 특정 단계로 되감기 — 그 단계 이후 답변은 모두 비운다 */
  const rewind = useCallback(
    (index: number) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setTyping(false);
      const next: GuideAnswers = { ...answers };
      GUIDE_STEPS.slice(index).forEach((step) => {
        delete next[step.key as keyof GuideAnswers];
        if (step.key === 'who') delete next.count;
        if (step.key === 'budget') delete next.budgetPer;
        // move 는 answers.move 가 아니라 transport/range 두 필드에 저장된다
        // when 은 answers.when 과 answers.days 두 필드에 저장된다
        if (step.key === 'when') delete next.days;
        if (step.key === 'move') {
          delete next.transport;
          delete next.rangeKm;
        }
      });
      onAnswersChange(next);
      setStepIndex(index);
    },
    [answers, onAnswersChange],
  );

  const reset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setTyping(false);
    onAnswersChange({});
    setStepIndex(0);
  }, [onAnswersChange]);

  return (
    // [2026-09-21] 내용은 위쪽 정렬을 유지한다.
    // [이유] 한 번 아래에서 위로 쌓는 방식(mt:auto)으로 바꿔봤으나, 첫 단계가 화면 하단에 붙어
    //        오히려 읽는 순서와 어긋났다(사용자 피드백: "하단에 뿌려져, 상단에 가야지").
    //        질문은 위에서부터 차례로 내려오는 편이 자연스럽다 — 되돌리지 말 것.
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '18px 26px 8px' }}>
      <GuideKeyframes />

      {/* 헤더 — 제목 + 진행 게이지 + 자유 입력 전환 */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: '14px' }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: INK_HI }}>
          여행 설계 가이드
        </Typography>
        <Stack direction="row" spacing="5px" aria-hidden="true">
          {GUIDE_STEPS.map((step, i) => (
            <Box
              key={step.key}
              sx={{
                width: i < stepIndex ? 26 : 22,
                height: 4,
                borderRadius: 999,
                transition: 'background .35s ease, width .35s ease',
                background: i < stepIndex ? `linear-gradient(90deg, ${BRAND}, ${ACCENT})` : PAPER2,
              }}
            />
          ))}
        </Stack>
      </Stack>

      {/* 단계 카드 */}
      {GUIDE_STEPS.slice(0, Math.min(stepIndex + 1, GUIDE_STEPS.length)).map((step, index) => (
        <StepCard
          key={step.key}
          step={step}
          answers={answers}
          done={index < stepIndex}
          onCommit={commit}
          onPatch={patch}
          onRewind={() => rewind(index)}
        />
      ))}

      {typing && <TypingRow />}

      {completed && (
        <FinalCard sentence={buildGuideSentence(answers)} onSubmit={onSubmit} onReset={reset} />
      )}

      <div ref={bottomRef} />
    </Box>
  );
}

// ─── 단계 카드 ────────────────────────────────────────────────────────────────

function StepCard({
  step,
  answers,
  done,
  onCommit,
  onPatch,
  onRewind,
}: {
  step: GuideStep;
  answers: GuideAnswers;
  done: boolean;
  onCommit: (patch: Partial<GuideAnswers>) => void;
  /** 진행 없이 값만 갱신(move 단계처럼 한 카드에서 두 값을 받는 경우) */
  onPatch: (patch: Partial<GuideAnswers>) => void;
  onRewind: () => void;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '12px',
        mb: '6px',
        animation: 'gwGuideIn .42s cubic-bezier(.2,.8,.25,1) both',
      }}
    >
      {/* 레일 선은 '다음 카드로 이어질 때'만 그린다 — 마지막(진행 중) 카드 아래로 선이 길게
          늘어져 화면이 끊긴 것처럼 보이던 문제(2026-09-21 디자인 QA) */}
      <Rail done={done} connected={done} />
      <Box sx={{ flex: 1, minWidth: 0, pb: done ? '12px' : '16px' }}>
        <Typography
          sx={{
            fontSize: done ? 12.5 : 15,
            fontWeight: done ? 600 : 800,
            color: done ? INK_MID : INK_HI,
            lineHeight: 1.45,
          }}
        >
          {step.question}
          {!done && step.badge && (
            <Box
              component="span"
              sx={{
                ml: '6px',
                fontSize: 10.5,
                fontWeight: 800,
                color: BRAND_TEXT,
                bgcolor: PAPER2,
                borderRadius: 999,
                px: '7px',
                py: '3px',
                verticalAlign: 'middle',
              }}
            >
              {step.badge}
            </Box>
          )}
        </Typography>
        {!done && (
          <Typography sx={{ fontSize: 11.5, color: INK_LOW, mt: '4px' }}>{step.hint}</Typography>
        )}

        {done ? (
          <Stack direction="row" alignItems="center" sx={{ mt: '10px' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                bgcolor: PAPER1,
                border: `1px solid ${SOFT_LINE}`,
                borderRadius: 999,
                px: '12px',
                py: '6px',
                fontSize: 12,
                fontWeight: 700,
                color: INK_HI,
              }}
            >
              {answerLabel(step.key, answers)}
            </Box>
            <ButtonBase
              onClick={onRewind}
              sx={{
                ml: '8px',
                fontSize: 11,
                color: INK_LOW,
                textDecoration: 'underline',
                p: '4px',
              }}
            >
              변경
            </ButtonBase>
          </Stack>
        ) : (
          <>
            <StepBody step={step} answers={answers} onCommit={onCommit} onPatch={onPatch} />
            <NoteBox text={step.note} />
          </>
        )}
      </Box>
    </Box>
  );
}

/** 왼쪽 진행 레일 — 점 + 이어지는 선 */
function Rail({ done, connected }: { done: boolean; connected: boolean }): JSX.Element {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 22,
        flex: '0 0 22px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: 11,
          height: 11,
          mt: '5px',
          borderRadius: '50%',
          bgcolor: done ? ACCENT : PAPER0,
          border: `2px solid ${done ? ACCENT : BRAND}`,
          animation: 'gwGuidePop .4s cubic-bezier(.2,1.4,.4,1) both',
        }}
      />
      {connected && (
        <Box
          sx={{
            flex: 1,
            width: 2,
            mt: '3px',
            background: `linear-gradient(180deg, ${BORDER}, ${SOFT_LINE})`,
          }}
        />
      )}
    </Box>
  );
}

function NoteBox({ text }: { text: string }): JSX.Element {
  return (
    <Stack
      direction="row"
      spacing="8px"
      sx={{
        mt: '12px',
        p: '10px 12px',
        borderRadius: `${tokens.radius.m}px`,
        // 브랜드 톤의 옅은 틴트 — 안내 박스가 본문 카드와 구분되게(hex 직접 입력 대신 alpha 파생)
        bgcolor: alpha(BRAND, 0.045),
        border: `1px dashed ${alpha(BRAND, 0.28)}`,
        animation: 'gwGuideChip .4s .3s cubic-bezier(.2,.8,.25,1) both',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          flex: '0 0 15px',
          width: 15,
          height: 15,
          mt: '1px',
          borderRadius: '50%',
          bgcolor: BRAND,
          color: PAPER0,
          fontSize: 10,
          fontWeight: 800,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        !
      </Box>
      <Typography sx={{ fontSize: 11.5, lineHeight: 1.6, color: INK_MID }}>{text}</Typography>
    </Stack>
  );
}

/** 보기(칩) / 인원 스테퍼 / 예산 슬라이더 분기 */
function StepBody({
  step,
  answers,
  onCommit,
  onPatch,
}: {
  step: GuideStep;
  answers: GuideAnswers;
  onCommit: (patch: Partial<GuideAnswers>) => void;
  /** 다음 단계로 넘기지 않고 값만 갱신 — 한 단계에서 여러 값을 받는 위젯(move)용 */
  onPatch: (patch: Partial<GuideAnswers>) => void;
}): JSX.Element {
  if (step.custom === 'budget') return <BudgetPicker answers={answers} onCommit={onCommit} />;
  if (step.custom === 'when')
    return <WhenPicker answers={answers} onCommit={onCommit} onPatch={onPatch} />;
  if (step.custom === 'detail') return <DetailPicker answers={answers} onCommit={onCommit} />;
  if (step.custom === 'move')
    return <MovePicker answers={answers} onCommit={onCommit} onPatch={onPatch} />;
  if (step.key === 'who') return <WhoPicker answers={answers} onCommit={onCommit} />;
  if (step.multi) return <MultiPicker step={step} answers={answers} onCommit={onCommit} />;

  const options = resolveOptions(step, answers);
  return (
    <SinglePicker
      step={step}
      options={options}
      onPick={(value) => onCommit({ [step.key]: value } as Partial<GuideAnswers>)}
    />
  );
}

/**
 * [목적] 단일 선택 단계 — 보기 칩 + '직접 입력하기'
 * [이유] 2026-09-21 QA: 보기에 없는 값을 채팅으로 유도하면 흐름이 끊긴다. 같은 자리에서 바로 적게 한다.
 */
function SinglePicker({
  step,
  options,
  onPick,
}: {
  step: GuideStep;
  options: ReturnType<typeof resolveOptions>;
  onPick: (value: string) => void;
}): JSX.Element {
  const [customOpen, setCustomOpen] = useState(false);

  return (
    <>
      <ChipRow>
        {options.map((option, i) => (
          <Chip
            key={option.value}
            delay={i * 45}
            label={option.value}
            sub={option.sub}
            onClick={() => onPick(option.value)}
          />
        ))}
        {step.customPlaceholder && !customOpen && (
          <Chip
            dashed
            delay={options.length * 45}
            label="직접 입력하기"
            onClick={() => setCustomOpen(true)}
          />
        )}
      </ChipRow>
      {customOpen && step.customPlaceholder && (
        <CustomInput
          placeholder={step.customPlaceholder}
          onCancel={() => setCustomOpen(false)}
          onSubmit={(value) => onPick(value)}
        />
      )}
    </>
  );
}

/** 동반자 → 선택 즉시 같은 카드 안에서 인원 입력으로 이어진다 */
function WhoPicker({
  answers,
  onCommit,
}: {
  answers: GuideAnswers;
  onCommit: (patch: Partial<GuideAnswers>) => void;
}): JSX.Element {
  const step = GUIDE_STEPS.find((s) => s.key === 'who') as GuideStep;
  const [picked, setPicked] = useState<string | null>(null);
  const [count, setCount] = useState(2);

  const pick = (value: string): void => {
    if (value === '혼자') {
      onCommit({ who: '혼자', count: 1 });
      return;
    }
    setPicked(value);
    setCount(value === '가족과' || value === '어르신·아이와' ? 3 : 2);
  };

  if (!picked) {
    return <SinglePicker step={step} options={resolveOptions(step, answers)} onPick={pick} />;
  }

  const isPet = picked === '반려견과';
  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ mt: '12px' }}>
        <Box
          sx={{
            bgcolor: PAPER1,
            border: `1px solid ${SOFT_LINE}`,
            borderRadius: 999,
            px: '12px',
            py: '6px',
            fontSize: 12,
            fontWeight: 700,
            color: INK_HI,
          }}
        >
          {picked}
        </Box>
        <ButtonBase
          onClick={() => setPicked(null)}
          sx={{ ml: '8px', fontSize: 11, color: INK_LOW, textDecoration: 'underline', p: '4px' }}
        >
          다시 고르기
        </ButtonBase>
      </Stack>

      <Box
        sx={{
          mt: '14px',
          p: '14px',
          borderRadius: `${tokens.radius.m}px`,
          bgcolor: PAPER1,
          border: `1px solid ${SOFT_LINE}`,
          animation: 'gwGuideIn .34s cubic-bezier(.2,.8,.25,1) both',
        }}
      >
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK_HI }}>
          {isPet ? '반려견을 빼고, 사람은 몇 명인가요?' : '총 몇 명이서 가나요?'}
          <Box
            component="span"
            sx={{
              ml: '6px',
              fontSize: 10.5,
              fontWeight: 800,
              color: BRAND_TEXT,
              bgcolor: PAPER2,
              borderRadius: 999,
              px: '7px',
              py: '3px',
              verticalAlign: 'middle',
            }}
          >
            본인 포함
          </Box>
        </Typography>

        <Stack
          direction="row"
          spacing="8px"
          alignItems="center"
          sx={{ mt: '11px', flexWrap: 'wrap' }}
        >
          <StepperButton
            label="−"
            ariaLabel="인원 줄이기"
            onClick={() => setCount((n) => Math.max(COUNT_MIN, n - 1))}
          />
          <Box
            sx={{ minWidth: 58, textAlign: 'center', fontSize: 17, fontWeight: 800, color: INK_HI }}
          >
            <Box component="span" sx={{ fontSize: 22, mr: '2px' }}>
              {count}
            </Box>
            명
          </Box>
          <StepperButton
            label="+"
            ariaLabel="인원 늘리기"
            onClick={() => setCount((n) => Math.min(COUNT_MAX, n + 1))}
          />
          <Stack direction="row" spacing="5px" sx={{ ml: '4px' }}>
            {[2, 3, 4, 5, 6].map((n) => (
              <ButtonBase
                key={n}
                onClick={() => setCount(n)}
                aria-label={`${n}명`}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '8px',
                  border: `1px solid ${count === n ? BRAND : BORDER}`,
                  bgcolor: count === n ? BRAND : PAPER0,
                  color: count === n ? PAPER0 : INK_MID,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {n}
              </ButtonBase>
            ))}
          </Stack>
          <PrimaryButton
            label="확인 →"
            onClick={() => onCommit({ who: picked, count })}
            sx={{ ml: 'auto' }}
          />
        </Stack>
      </Box>
    </>
  );
}

/** 복수 선택 단계 — 최대 MOOD_MAX(무드)/제한 없음(이동 조건) */
function MultiPicker({
  step,
  answers,
  onCommit,
}: {
  step: GuideStep;
  answers: GuideAnswers;
  onCommit: (patch: Partial<GuideAnswers>) => void;
}): JSX.Element {
  const [picked, setPicked] = useState<string[]>([]);
  const [customOpen, setCustomOpen] = useState(false);
  const limit = step.key === 'mood' ? MOOD_MAX : Number.POSITIVE_INFINITY;

  const toggle = (value: string): void => {
    setPicked((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= limit) return prev;
      return [...prev, value];
    });
  };

  const options = resolveOptions(step, answers);
  const extras = picked.filter((v) => !options.some((o) => o.value === v));
  return (
    <>
      <ChipRow>
        {options.map((option, i) => (
          <Chip
            key={option.value}
            delay={i * 45}
            label={option.value}
            sub={option.sub}
            selected={picked.includes(option.value)}
            onClick={() => toggle(option.value)}
          />
        ))}
        {/* 직접 입력으로 추가한 항목 — 다시 누르면 빠진다 */}
        {extras.map((value) => (
          <Chip key={value} selected label={value} onClick={() => toggle(value)} />
        ))}
        {step.customPlaceholder && !customOpen && (
          <Chip dashed label="직접 입력하기" onClick={() => setCustomOpen(true)} />
        )}
        {picked.length > 0 && (
          <Chip
            selected
            label={`다 골랐어요 (${picked.length})`}
            onClick={() => onCommit({ [step.key]: picked } as Partial<GuideAnswers>)}
          />
        )}
        {step.optional && (
          // 빈 배열 = '해당 없어요'(답변함). 문자열 '해당 없음' 을 쓰면 보기에 '·' 가 있는 항목과
          // 구분자가 충돌했던 과거 버그가 되살아난다(guideSteps.ts GuideAnswers 주석 참조).
          <Chip
            dashed
            label="해당 없어요"
            onClick={() => onCommit({ [step.key]: [] } as Partial<GuideAnswers>)}
          />
        )}
      </ChipRow>
      {customOpen && step.customPlaceholder && (
        <CustomInput
          placeholder={step.customPlaceholder}
          onCancel={() => setCustomOpen(false)}
          onSubmit={(value) => {
            setPicked((prev) =>
              prev.includes(value) || prev.length >= limit ? prev : [...prev, value],
            );
            setCustomOpen(false);
          }}
        />
      )}
    </>
  );
}

/** 예산 — 슬라이더 + 직접 입력 + 퀵 버튼이 모두 같은 값에 연동 */
/**
 * [목적] 이동 계획 위젯 — 이동수단 + 이동 반경을 한 카드에서 함께 받는다
 * [이유] [2026-09-21] 두 질문은 "어떻게 이동하고 얼마나 멀리 갈지"라는 하나의 판단이다. 따로 물으면
 *        단계만 늘고 맥락이 끊긴다. 특히 반경은 이동수단을 봐야 정할 수 있어서, 같은 화면에서
 *        수단을 고르는 즉시 반경 보기의 보조 설명이 바뀌는 편이 훨씬 고르기 쉽다
 *        (예: 30km → 자차면 '시군 전체', 버스면 '환승 각오').
 * [사이드 이팩트] 두 값이 모두 채워졌을 때만 onCommit(다음 단계로 진행) — 그 전에는 onPatch 로 값만 갱신.
 * [수정 시 고려사항] 보기 문자열은 BE 계약(TransportEnum 라벨 / RANGE_PREFERENCE_META.label)과 동일해야
 *                   한다 — 문장으로 조립돼 슬롯 추출로 들어가므로 한 글자만 달라도 매칭이 깨진다.
 */
function MovePicker({
  answers,
  onCommit,
  onPatch,
}: {
  answers: GuideAnswers;
  onCommit: (patch: Partial<GuideAnswers>) => void;
  onPatch: (patch: Partial<GuideAnswers>) => void;
}): JSX.Element {
  const pick = (next: Partial<GuideAnswers>) => {
    const merged = { ...answers, ...next };
    // 두 값이 다 차면 진행, 아니면 값만 갱신
    if (merged.transport && merged.rangeKm !== undefined) onCommit(next);
    else onPatch(next);
  };

  const km = answers.rangeKm ?? 15;
  const unlimited = answers.rangeKm === RANGE_UNLIMITED;
  const fill =
    ((Math.min(RANGE_SLIDER_MAX, Math.max(RANGE_SLIDER_MIN, km)) - RANGE_SLIDER_MIN) /
      (RANGE_SLIDER_MAX - RANGE_SLIDER_MIN)) *
    100;

  return (
    <Stack spacing={1.6}>
      {/* ── 이동수단 ── */}
      <Stack spacing={0.8}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: INK_MID }}>이동수단</Typography>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '8px' }}>
          {TRANSPORT_OPTIONS.map((o: GuideOption) => {
            const on = answers.transport === o.value;
            return (
              <ButtonBase
                key={o.value}
                onClick={() => pick({ transport: o.value })}
                aria-pressed={on}
                sx={{
                  px: '14px',
                  py: '8px',
                  minHeight: 44,
                  borderRadius: `${tokens.radius.m}px`,
                  border: `1px solid ${on ? BRAND : BORDER}`,
                  bgcolor: on ? alpha(BRAND, 0.08) : PAPER0,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: on ? BRAND_TEXT : INK_HI }}>
                  {o.value}
                </Typography>
                {o.sub && (
                  <Typography sx={{ fontSize: 10.5, color: INK_MID, mt: '1px' }}>
                    {o.sub}
                  </Typography>
                )}
              </ButtonBase>
            );
          })}
        </Stack>
      </Stack>

      {/* ── 이동 반경: 예산 위젯과 같은 조작(직접 입력 + 슬라이더 + 퀵 버튼) ──
          [2026-09-21] 칩 4종으로는 "20km쯤"을 표현할 수 없어 연속값 조작으로 바꿨다(사용자 요청). */}
      <Stack spacing={0.8}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: INK_MID }}>
          어디까지 다닐까요?
        </Typography>
        <Box
          sx={{
            p: '15px 16px',
            borderRadius: `${tokens.radius.m}px`,
            bgcolor: PAPER1,
            border: `1px solid ${SOFT_LINE}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing="9px">
            <Box
              component="span"
              sx={{
                fontSize: 10.5,
                fontWeight: 800,
                color: BRAND_TEXT,
                bgcolor: PAPER2,
                borderRadius: 999,
                px: '8px',
                py: '4px',
              }}
            >
              거점 기준
            </Box>
            <Stack direction="row" alignItems="baseline" spacing="3px">
              <TextField
                variant="standard"
                type="number"
                value={unlimited ? '' : km}
                disabled={unlimited}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  if (Number.isNaN(next)) return;
                  pick({
                    rangeKm: Math.max(1, Math.min(RANGE_INPUT_MAX, Math.round(next))),
                  });
                }}
                inputProps={{
                  'aria-label': '이동 반경(km)',
                  min: 1,
                  max: RANGE_INPUT_MAX,
                  style: { textAlign: 'right' },
                }}
                sx={{
                  width: 66,
                  '& input': { fontSize: 26, fontWeight: 800, color: INK_HI, padding: '0 2px 2px' },
                  '& .MuiInput-underline:before': { borderBottom: `2px solid ${BORDER}` },
                  '& .MuiInput-underline:after': { borderBottom: `2px solid ${BRAND}` },
                }}
              />
              <Box component="span" sx={{ fontSize: 13, fontWeight: 700, color: INK_MID }}>
                km 이내
              </Box>
            </Stack>
            <ButtonBase
              onClick={() => pick({ rangeKm: RANGE_UNLIMITED })}
              aria-pressed={unlimited}
              sx={{
                ml: 'auto',
                px: '10px',
                minHeight: 32,
                borderRadius: 999,
                border: `1px ${unlimited ? 'solid' : 'dashed'} ${unlimited ? BRAND : BORDER}`,
                bgcolor: unlimited ? alpha(BRAND, 0.08) : 'transparent',
                fontSize: 11,
                fontWeight: 700,
                color: unlimited ? BRAND_TEXT : INK_MID,
              }}
            >
              거리 상관없음
            </ButtonBase>
          </Stack>

          <Box
            component="input"
            type="range"
            min={RANGE_SLIDER_MIN}
            max={RANGE_SLIDER_MAX}
            step={RANGE_SLIDER_STEP}
            value={Math.max(RANGE_SLIDER_MIN, Math.min(RANGE_SLIDER_MAX, km))}
            aria-label="이동 반경 슬라이더"
            disabled={unlimited}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              pick({ rangeKm: Number(e.target.value) })
            }
            sx={{
              WebkitAppearance: 'none',
              appearance: 'none',
              width: '100%',
              height: 7,
              borderRadius: 999,
              outline: 'none',
              cursor: unlimited ? 'not-allowed' : 'pointer',
              opacity: unlimited ? 0.4 : 1,
              m: '14px 0 6px',
              background: `linear-gradient(90deg, ${BRAND} 0%, ${ACCENT} ${fill}%, ${PAPER2} ${fill}%, ${PAPER2} 100%)`,
              '&::-webkit-slider-thumb': {
                WebkitAppearance: 'none',
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: PAPER0,
                border: `3px solid ${BRAND}`,
                cursor: 'grab',
              },
              '&::-webkit-slider-thumb:active': { cursor: 'grabbing', transform: 'scale(1.15)' },
            }}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ fontSize: 9.5, color: INK_LOW, fontFamily: MONO }}
          >
            <span>1km</span>
            <span>15km</span>
            <span>30km</span>
            <span>{RANGE_SLIDER_MAX}km+</span>
          </Stack>

          <Stack direction="row" spacing="6px" sx={{ mt: '11px', flexWrap: 'wrap' }}>
            {rangePresets(answers.transport).map((preset) => (
              <ButtonBase
                key={preset.label}
                onClick={() => pick({ rangeKm: preset.km })}
                sx={{
                  flex: 1,
                  minWidth: 66,
                  flexDirection: 'column',
                  border: `1px solid ${answers.rangeKm === preset.km ? BRAND : BORDER}`,
                  bgcolor: PAPER0,
                  borderRadius: `${tokens.radius.m}px`,
                  p: '8px 6px',
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: INK_HI,
                  '&:hover': { borderColor: BRAND, color: BRAND_TEXT },
                }}
              >
                {preset.label}
                <Box
                  component="span"
                  sx={{ fontSize: 10, fontWeight: 600, color: INK_LOW, mt: '2px' }}
                >
                  {preset.km}km · {preset.sub}
                </Box>
              </ButtonBase>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
}

/**
 * [목적] 일정 위젯 — 출발 날짜 + 머무는 기간을 한 카드에서 함께 받는다
 * [이유] [2026-09-21] "언제 떠나서 며칠 있다 오는지"는 하나의 일정 판단이다. 따로 물으면 단계만 늘고
 *        날짜를 고른 직후 기간을 고르는 흐름이 끊긴다(move 단계와 같은 이유).
 *        날짜는 네이티브 date 인풋을 그대로 노출하면 빈 상태가 "연도. 월. 일."로 보여 톤이 어긋나므로
 *        칩으로 감싸고 showPicker() 로 달력을 띄운다.
 * [사이드 이팩트] 두 값이 모두 채워졌을 때만 onCommit(다음 단계) — 그 전에는 onPatch 로 값만 갱신.
 * [수정 시 고려사항] '이번 주말' 계산은 BE relative-date.ts 와 같은 규칙(다가오는 토요일, 토요일이면 오늘).
 *                   두 곳이 어긋나면 가이드로 고른 날짜와 문장 해석이 달라진다.
 */
function WhenPicker({
  answers,
  onCommit,
  onPatch,
}: {
  answers: GuideAnswers;
  onCommit: (patch: Partial<GuideAnswers>) => void;
  onPatch: (patch: Partial<GuideAnswers>) => void;
}): JSX.Element {
  const toIso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = new Date();
  const plusDays = (n: number) => {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    d.setDate(d.getDate() + n);
    return d;
  };
  // 다가오는 토요일 — 오늘이 토요일이면 오늘(BE relative-date.ts 와 동일 규칙)
  const comingSaturday = () => plusDays((6 - today.getDay() + 7) % 7);

  const quick = [
    { label: '오늘', iso: toIso(plusDays(0)) },
    { label: '내일', iso: toIso(plusDays(1)) },
    { label: '이번 주말', iso: toIso(comingSaturday()) },
  ];
  const selected = answers.when ?? '';
  const isQuick = quick.some((q) => q.iso === selected);
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);
  const [daysCustomOpen, setDaysCustomOpen] = useState(false);
  const [daysDraft, setDaysDraft] = useState('');

  const pick = (next: Partial<GuideAnswers>) => {
    const merged = { ...answers, ...next };
    if (merged.when !== undefined && merged.days) onCommit(next);
    else onPatch(next);
  };

  const chip = (on: boolean, dashed = false) => ({
    px: '14px',
    minHeight: 44,
    borderRadius: 999,
    border: `1px ${dashed ? 'dashed' : 'solid'} ${on ? BRAND : BORDER}`,
    bgcolor: on ? alpha(BRAND, 0.08) : PAPER0,
    fontSize: 13,
    fontWeight: on ? 800 : 600,
    color: on ? BRAND_TEXT : INK_HI,
  });

  return (
    <Stack spacing={1.6}>
      {/* ── 출발 날짜 ── */}
      <Stack spacing={0.8}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: INK_MID }}>출발 날짜</Typography>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '8px' }}>
          {quick.map((q) => (
            <ButtonBase
              key={q.label}
              onClick={() => pick({ when: q.iso })}
              aria-pressed={selected === q.iso}
              sx={chip(selected === q.iso)}
            >
              {q.label}
            </ButtonBase>
          ))}
          <ButtonBase
            onClick={(e) => setCalendarAnchor(e.currentTarget)}
            aria-label="달력에서 날짜 선택"
            aria-haspopup="dialog"
            sx={chip(Boolean(selected) && !isQuick)}
          >
            {selected && !isQuick ? formatGuideDate(selected) : '날짜 직접 고르기'}
          </ButtonBase>
          {/* [2026-09-21] 네이티브 showPicker() 대신 직접 그린 캘린더.
              OS 기본 달력은 파란 시스템 하이라이트·'삭제/오늘' 버튼·시스템 폰트가 그대로 나와
              앱 톤과 완전히 어긋났다(사용자 피드백). GuideCalendar 가 같은 토큰으로 그린다. */}
          <GuideCalendar
            value={selected || undefined}
            min={toIso(plusDays(0))}
            anchorEl={calendarAnchor}
            onClose={() => setCalendarAnchor(null)}
            onSelect={(iso) => pick({ when: iso })}
          />
          <ButtonBase
            onClick={() => pick({ when: null })}
            aria-pressed={answers.when === null}
            sx={chip(answers.when === null, true)}
          >
            아직 미정
          </ButtonBase>
        </Stack>
      </Stack>

      {/* ── 머무는 기간 ── */}
      <Stack spacing={0.8}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: INK_MID }}>
          며칠 머무시나요?
        </Typography>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '8px' }}>
          {DAYS_OPTIONS.map((d) => (
            <ButtonBase
              key={d}
              onClick={() => pick({ days: d })}
              aria-pressed={answers.days === d}
              sx={chip(answers.days === d)}
            >
              {d}
            </ButtonBase>
          ))}
          {!daysCustomOpen && (
            <ButtonBase
              onClick={() => setDaysCustomOpen(true)}
              sx={chip(Boolean(answers.days) && !DAYS_OPTIONS.includes(answers.days ?? ''), true)}
            >
              {answers.days && !DAYS_OPTIONS.includes(answers.days) ? answers.days : '직접 입력'}
            </ButtonBase>
          )}
        </Stack>
        {daysCustomOpen && (
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              autoFocus
              placeholder="예: 5박 6일"
              value={daysDraft}
              onChange={(e) => setDaysDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && daysDraft.trim()) {
                  setDaysCustomOpen(false);
                  pick({ days: daysDraft.trim() });
                }
              }}
              sx={{ maxWidth: 200 }}
            />
            <ButtonBase
              onClick={() => {
                if (!daysDraft.trim()) return;
                setDaysCustomOpen(false);
                pick({ days: daysDraft.trim() });
              }}
              sx={chip(false)}
            >
              확인
            </ButtonBase>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

/**
 * [목적] 마지막 자유 요청 위젯 — 텍스트 입력 + 예시 칩 + '없어요'
 * [이유] [2026-09-21] 예전에는 BE 가 이 질문을 채팅 버블로 던졌는데, 가이드로 여기까지 온 사용자에게
 *        갑자기 채팅 화면이 열리는 게 흐름을 깼다(사용자 요청: 채팅 제거). 같은 질문을 가이드 안에서 받는다.
 * [사이드 이팩트] 값은 문장 끝에 그대로 덧붙는다(buildGuideSentence) — 가공하지 않는다.
 * [수정 시 고려사항] '없어요'는 null 로 저장한다 — undefined(아직 안 물음)와 구분해야 완료 판정이 된다.
 */
function DetailPicker({
  answers,
  onCommit,
}: {
  answers: GuideAnswers;
  onCommit: (patch: Partial<GuideAnswers>) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(answers.detail ?? '');
  const submit = () => {
    const text = draft.trim();
    onCommit({ detail: text ? text : null });
  };

  return (
    <Stack spacing={1.2}>
      <TextField
        multiline
        minRows={2}
        maxRows={4}
        placeholder="예: 주문진 수산시장은 꼭 가고 싶어요"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            submit();
          }
        }}
        inputProps={{ 'aria-label': '추가로 넣고 싶은 요청' }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: `${tokens.radius.m}px`, fontSize: 13 } }}
      />

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '6px' }}>
        {DETAIL_EXAMPLES.map((ex) => (
          <ButtonBase
            key={ex}
            onClick={() => setDraft((cur) => (cur.trim() ? `${cur.trim()}, ${ex}` : ex))}
            sx={{
              px: '10px',
              minHeight: 30,
              borderRadius: 999,
              border: `1px dashed ${BORDER}`,
              fontSize: 11,
              fontWeight: 600,
              color: INK_MID,
            }}
          >
            {ex}
          </ButtonBase>
        ))}
      </Stack>

      <Stack direction="row" spacing={1}>
        <ButtonBase
          onClick={submit}
          disabled={!draft.trim()}
          sx={{
            px: '18px',
            minHeight: 44,
            borderRadius: 999,
            bgcolor: draft.trim() ? BRAND : PAPER2,
            color: draft.trim() ? PAPER0 : INK_LOW,
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          이대로 반영
        </ButtonBase>
        <ButtonBase
          onClick={() => onCommit({ detail: null })}
          sx={{
            px: '18px',
            minHeight: 44,
            borderRadius: 999,
            border: `1px solid ${BORDER}`,
            fontSize: 13,
            fontWeight: 700,
            color: INK_MID,
          }}
        >
          없어요
        </ButtonBase>
      </Stack>
    </Stack>
  );
}

function BudgetPicker({
  answers,
  onCommit,
}: {
  answers: GuideAnswers;
  onCommit: (patch: Partial<GuideAnswers>) => void;
}): JSX.Element {
  const [perPerson, setPerPerson] = useState(BUDGET_DEFAULT);
  const count = answers.count ?? 1;
  const { tier, label } = budgetTier(perPerson);
  const tierColor = TIER_COLOR[tier];
  const fillPercent =
    ((Math.min(BUDGET_MAX, Math.max(BUDGET_MIN, perPerson)) - BUDGET_MIN) /
      (BUDGET_MAX - BUDGET_MIN)) *
    100;

  return (
    <Box
      sx={{
        mt: '12px',
        p: '15px 16px',
        borderRadius: `${tokens.radius.m}px`,
        bgcolor: PAPER1,
        border: `1px solid ${SOFT_LINE}`,
        animation: 'gwGuideIn .34s cubic-bezier(.2,.8,.25,1) both',
      }}
    >
      <Stack direction="row" alignItems="center" spacing="9px">
        <Box
          component="span"
          sx={{
            fontSize: 10.5,
            fontWeight: 800,
            color: BRAND_TEXT,
            bgcolor: PAPER2,
            borderRadius: 999,
            px: '8px',
            py: '4px',
          }}
        >
          1인 기준
        </Box>
        <Stack direction="row" alignItems="baseline" spacing="3px">
          <TextField
            variant="standard"
            type="number"
            value={perPerson}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (Number.isNaN(next)) return;
              setPerPerson(Math.max(1, Math.min(BUDGET_INPUT_MAX, Math.round(next))));
            }}
            inputProps={{
              'aria-label': '1인 예산(만원)',
              min: 1,
              max: BUDGET_INPUT_MAX,
              style: { textAlign: 'right' },
            }}
            sx={{
              width: 74,
              '& input': { fontSize: 26, fontWeight: 800, color: INK_HI, padding: '0 2px 2px' },
              '& .MuiInput-underline:before': { borderBottom: `2px solid ${BORDER}` },
              '& .MuiInput-underline:after': { borderBottom: `2px solid ${BRAND}` },
            }}
          />
          <Box component="span" sx={{ fontSize: 13, fontWeight: 700, color: INK_MID }}>
            만원
          </Box>
        </Stack>
        <Box
          component="span"
          sx={{
            ml: 'auto',
            fontSize: 11,
            fontWeight: 800,
            color: tierColor,
            bgcolor: PAPER2,
            borderRadius: 999,
            px: '10px',
            py: '5px',
          }}
        >
          {label}
        </Box>
      </Stack>

      <Box
        component="input"
        type="range"
        min={BUDGET_MIN}
        max={BUDGET_MAX}
        step={BUDGET_STEP}
        value={Math.max(BUDGET_MIN, Math.min(BUDGET_MAX, perPerson))}
        aria-label="1인 예산 슬라이더"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPerPerson(Number(e.target.value))}
        sx={{
          WebkitAppearance: 'none',
          appearance: 'none',
          width: '100%',
          height: 7,
          borderRadius: 999,
          outline: 'none',
          cursor: 'pointer',
          m: '14px 0 6px',
          background: `linear-gradient(90deg, ${BRAND} 0%, ${ACCENT} ${fillPercent}%, ${PAPER2} ${fillPercent}%, ${PAPER2} 100%)`,
          '&::-webkit-slider-thumb': {
            WebkitAppearance: 'none',
            width: 22,
            height: 22,
            borderRadius: '50%',
            bgcolor: PAPER0,
            border: `3px solid ${BRAND}`,
            cursor: 'grab',
          },
          '&::-webkit-slider-thumb:active': { cursor: 'grabbing', transform: 'scale(1.15)' },
        }}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ fontSize: 9.5, color: INK_LOW, fontFamily: MONO }}
      >
        <span>{BUDGET_MIN}만</span>
        <span>30만</span>
        <span>60만</span>
        <span>{BUDGET_MAX}만+</span>
      </Stack>

      <Box
        sx={{
          mt: '12px',
          p: '10px 12px',
          borderRadius: `${tokens.radius.m}px`,
          bgcolor: PAPER0,
          border: `1px solid ${BORDER}`,
          fontSize: 12,
          fontWeight: 600,
          color: INK_MID,
        }}
      >
        총{' '}
        <Box component="b" sx={{ color: INK_HI, fontWeight: 800 }}>
          {count}
        </Box>
        명 예상 예산
        <Box component="b" sx={{ ml: '4px', color: BRAND_TEXT, fontSize: 14, fontWeight: 800 }}>
          약 {(perPerson * count).toLocaleString('ko-KR')}만원
        </Box>
      </Box>

      <Stack direction="row" spacing="6px" sx={{ mt: '11px', flexWrap: 'wrap' }}>
        {BUDGET_PRESETS.map((preset) => (
          <ButtonBase
            key={preset.label}
            onClick={() => setPerPerson(preset.value)}
            sx={{
              flex: 1,
              minWidth: 66,
              flexDirection: 'column',
              border: `1px solid ${BORDER}`,
              bgcolor: PAPER0,
              borderRadius: `${tokens.radius.m}px`,
              p: '8px 6px',
              fontSize: 11.5,
              fontWeight: 700,
              color: INK_HI,
              '&:hover': { borderColor: BRAND, color: BRAND_TEXT },
            }}
          >
            {preset.label}
            <Box component="span" sx={{ fontSize: 10, fontWeight: 600, color: INK_LOW, mt: '2px' }}>
              {preset.value}만
            </Box>
          </ButtonBase>
        ))}
      </Stack>

      <Stack direction="row" spacing="8px" alignItems="center" sx={{ mt: '13px' }}>
        <ButtonBase
          onClick={() => onCommit({ budgetPer: null })}
          sx={{
            flex: 1,
            height: 40,
            borderRadius: `${tokens.radius.m}px`,
            border: `1px solid ${BORDER}`,
            bgcolor: PAPER0,
            fontSize: 12.5,
            fontWeight: 700,
            color: INK_MID,
          }}
        >
          예산은 상관없어요
        </ButtonBase>
        <PrimaryButton label="확인 →" onClick={() => onCommit({ budgetPer: perPerson })} />
      </Stack>
    </Box>
  );
}

// ─── 완성 카드 ────────────────────────────────────────────────────────────────

function FinalCard({
  sentence,
  onSubmit,
  onReset,
}: {
  sentence: string;
  onSubmit: (sentence: string) => void;
  onReset: () => void;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '12px',
        animation: 'gwGuideIn .45s cubic-bezier(.2,.8,.25,1) both',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{ width: 22, flex: '0 0 22px', display: 'flex', justifyContent: 'center' }}
      >
        <Box
          sx={{
            width: 11,
            height: 11,
            mt: '5px',
            borderRadius: '50%',
            bgcolor: ACCENT,
            border: `2px solid ${ACCENT}`,
          }}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          border: `1px solid ${BORDER}`,
          borderRadius: `${tokens.radius.l}px`,
          p: '16px',
          background: `linear-gradient(160deg, ${alpha(BRAND, 0.06)}, ${PAPER0} 60%)`,
        }}
      >
        <Typography
          sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', color: BRAND_TEXT }}
        >
          READY TO DRAW
        </Typography>
        <Typography
          sx={{ fontSize: 14, fontWeight: 700, color: INK_HI, lineHeight: 1.65, mt: '8px' }}
        >
          “{sentence}”
        </Typography>
        <ButtonBase
          onClick={() => onSubmit(sentence)}
          sx={{
            mt: '14px',
            width: '100%',
            height: 46,
            borderRadius: `${tokens.radius.m}px`,
            color: PAPER0,
            fontSize: 13.5,
            fontWeight: 800,
            background: `linear-gradient(135deg, ${BRAND}, ${ACCENT})`,
          }}
        >
          이 문장으로 폴리오 그리기 →
        </ButtonBase>
        <ButtonBase
          onClick={onReset}
          sx={{
            mt: '9px',
            width: '100%',
            fontSize: 11.5,
            color: INK_LOW,
            textDecoration: 'underline',
          }}
        >
          처음부터 다시 고르기
        </ButtonBase>
      </Box>
    </Box>
  );
}

// ─── 공용 소품 ────────────────────────────────────────────────────────────────

/**
 * [목적] '직접 입력하기' 인라인 입력 — 보기에 없는 값을 그 자리에서 적는다
 * [이유] 2026-09-21 QA: "채팅으로 적어주세요" 안내는 단계 흐름을 끊고, 어디에 적어야 하는지도 애매했다.
 * [사이드 이팩트] 없음 — Enter 또는 '확인'으로 제출, 빈 값은 무시.
 */
function CustomInput({
  placeholder,
  onSubmit,
  onCancel,
}: {
  placeholder: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const submit = (): void => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    setText('');
  };

  return (
    <Stack
      direction="row"
      spacing="8px"
      alignItems="center"
      sx={{ mt: '10px', animation: 'gwGuideIn .28s cubic-bezier(.2,.8,.25,1) both' }}
    >
      <TextField
        autoFocus
        fullWidth
        size="small"
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // IME 조합 중 Enter 는 무시(한글 입력 마지막 글자 중복 방지 — ChatPage 관례와 동일)
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            submit();
          }
          if (e.key === 'Escape') onCancel();
        }}
        inputProps={{ 'aria-label': '직접 입력' }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: PAPER0,
            borderRadius: `${tokens.radius.m}px`,
            fontSize: 12.5,
          },
        }}
      />
      <PrimaryButton label="확인" onClick={submit} />
      <ButtonBase
        onClick={onCancel}
        sx={{
          flexShrink: 0,
          whiteSpace: 'nowrap',
          fontSize: 11.5,
          color: INK_LOW,
          textDecoration: 'underline',
          p: '4px',
        }}
      >
        취소
      </ButtonBase>
    </Stack>
  );
}

function ChipRow({ children }: { children: React.ReactNode }): JSX.Element {
  return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '7px', mt: '12px' }}>{children}</Box>;
}

function Chip({
  label,
  sub,
  onClick,
  selected = false,
  dashed = false,
  delay = 0,
}: {
  label: string;
  sub?: string;
  onClick: () => void;
  selected?: boolean;
  dashed?: boolean;
  delay?: number;
}): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        flexDirection: 'column',
        bgcolor: selected ? BRAND : PAPER0,
        border: selected ? '1px solid transparent' : `1px ${dashed ? 'dashed' : 'solid'} ${BORDER}`,
        borderRadius: 999,
        px: '14px',
        py: '9px',
        minHeight: 38,
        fontSize: 12.5,
        fontWeight: 600,
        color: selected ? PAPER0 : dashed ? INK_LOW : INK_HI,
        animation: `gwGuideChip .34s ${delay}ms cubic-bezier(.2,.8,.25,1) both`,
        transition: 'transform .16s ease, border-color .16s ease, color .16s ease',
        '&:hover': { transform: 'translateY(-2px)', borderColor: BRAND },
        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
      }}
    >
      {label}
      {sub && (
        <Box component="span" sx={{ fontSize: 10, fontWeight: 600, opacity: 0.62, mt: '1px' }}>
          {sub}
        </Box>
      )}
    </ButtonBase>
  );
}

function StepperButton({
  label,
  ariaLabel,
  onClick,
}: {
  label: string;
  ariaLabel: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={ariaLabel}
      sx={{
        width: 34,
        height: 34,
        borderRadius: '9px',
        border: `1px solid ${BORDER}`,
        bgcolor: PAPER0,
        fontSize: 17,
        fontWeight: 700,
        color: INK_HI,
        '&:hover': { borderColor: BRAND, color: BRAND_TEXT },
      }}
    >
      {label}
    </ButtonBase>
  );
}

function PrimaryButton({
  label,
  onClick,
  sx,
}: {
  label: string;
  onClick: () => void;
  sx?: Record<string, unknown>;
}): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        height: 34,
        px: '15px',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        borderRadius: '9px',
        color: PAPER0,
        fontSize: 12.5,
        fontWeight: 800,
        background: `linear-gradient(135deg, ${BRAND}, ${ACCENT})`,
        ...sx,
      }}
    >
      {label}
    </ButtonBase>
  );
}

function TypingRow(): JSX.Element {
  return (
    <Box
      role="status"
      aria-label="다음 질문을 준비하는 중"
      sx={{ display: 'flex', gap: '12px', animation: 'gwGuideIn .3s ease both' }}
    >
      <Box
        aria-hidden="true"
        sx={{ width: 22, flex: '0 0 22px', display: 'flex', justifyContent: 'center' }}
      >
        <Box
          sx={{
            width: 11,
            height: 11,
            mt: '5px',
            borderRadius: '50%',
            border: `2px solid ${BORDER}`,
          }}
        />
      </Box>
      <Stack
        direction="row"
        spacing="4px"
        alignItems="center"
        sx={{
          bgcolor: PAPER1,
          border: `1px solid ${SOFT_LINE}`,
          borderRadius: `${tokens.radius.m}px`,
          px: '13px',
          py: '10px',
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: INK_MID,
              animation: `gwGuideBlink 1.1s ${i * 0.18}s infinite`,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

/**
 * [목적] 가이드 전용 keyframes — 이 컴포넌트에서만 쓰는 진입/팝/깜빡임 모션
 * [이유] ChatPage.tsx 가 이미 같은 방식(<style> 전역 keyframes)으로 gwBlink 를 정의해 둔 관례를 따른다.
 * [수정 시 고려사항] 이름 충돌을 피하려고 gwGuide* 접두사를 쓴다.
 */
function GuideKeyframes(): JSX.Element {
  return (
    <style>{`
@keyframes gwGuideIn{from{opacity:0;transform:translateY(16px) scale(.985)}to{opacity:1;transform:none}}
@keyframes gwGuidePop{0%{transform:scale(.4);opacity:0}60%{transform:scale(1.18)}100%{transform:scale(1);opacity:1}}
@keyframes gwGuideChip{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@keyframes gwGuideBlink{0%,80%,100%{opacity:.25}40%{opacity:1}}
@media (prefers-reduced-motion: reduce){
  [class*="MuiBox"],[class*="MuiButtonBase"]{animation-duration:.01ms!important}
}
`}</style>
  );
}
